import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Users, Clock, DollarSign, CheckCircle, Info as InfoIcon, BookOpen } from 'lucide-react';
import { getBatches, getUserBatches, checkBatchEnrollment, enrollStudentInBatch, enrollStudentInBatchWithPayment } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import PaymentModal from '../payment/PaymentModal';
import toast from 'react-hot-toast';

const BatchListingModal = ({ userId, onClose, onEnrollmentSuccess }) => {
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [enrollingBatchId, setEnrollingBatchId] = useState(null);

  useEffect(() => {
    loadBatches();
  }, [userId]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      
      // Check if user has joined any batch
      let hasJoinedBatch = false;
      if (userId) {
        const userBatchesResult = await getUserBatches(userId);
        if (userBatchesResult.success && userBatchesResult.data && userBatchesResult.data.length > 0) {
          hasJoinedBatch = true;
        }
      }
      
      const result = await getBatches();
      if (result.success) {
        let filteredBatches = result.data;
        
        // If user hasn't joined any batch, only show admin-created batches (no coachingId)
        if (!hasJoinedBatch) {
          filteredBatches = result.data.filter(batch => !batch.coachingId);
        }
        
        // Filter out batches that are already enrolled or check enrollment status
        const batchesWithEnrollment = await Promise.all(
          filteredBatches.map(async (batch) => {
            if (!userId) return { ...batch, isEnrolled: false };
            const enrollmentCheck = await checkBatchEnrollment(batch.id, userId);
            return {
              ...batch,
              isEnrolled: enrollmentCheck.success && enrollmentCheck.enrolled
            };
          })
        );
        setBatches(batchesWithEnrollment);
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEnrollClick = (batch) => {
    if (!currentUser) {
      toast.error('Please login to enroll');
      return;
    }

    if (batch.price && batch.price > 0) {
      // Show payment modal
      setSelectedBatch(batch);
      setEnrollingBatchId(batch.id);
      setShowPaymentModal(true);
    } else {
      // Free enrollment
      handleFreeEnrollment(batch.id);
    }
  };

  const handleFreeEnrollment = async (batchId) => {
    try {
      if (!currentUser?.email) {
        toast.error('Email not found');
        return;
      }

      const result = await enrollStudentInBatch(batchId, currentUser.email);
      if (result.success) {
        toast.success('Successfully enrolled in batch!');
        // Reload batches to update the list (especially if this was first enrollment)
        await loadBatches();
        onEnrollmentSuccess?.();
      } else {
        toast.error(result.error || 'Failed to enroll');
      }
    } catch (error) {
      toast.error('Failed to enroll');
    }
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      if (!enrollingBatchId || !currentUser?.email) {
        toast.error('Missing enrollment information');
        return;
      }

      // Enroll student after successful payment
      const result = await enrollStudentInBatchWithPayment(
        enrollingBatchId,
        currentUser.email,
        {
          paymentId: paymentResponse.razorpay_payment_id,
          orderId: paymentResponse.razorpay_order_id,
          signature: paymentResponse.razorpay_signature,
          amount: selectedBatch.price,
          currency: 'INR'
        }
      );

      if (result.success) {
        toast.success('Successfully enrolled in batch!');
        setShowPaymentModal(false);
        setSelectedBatch(null);
        setEnrollingBatchId(null);
        // Reload batches to update the list (especially if this was first enrollment)
        await loadBatches();
        onEnrollmentSuccess?.();
      } else {
        toast.error(result.error || 'Failed to complete enrollment');
      }
    } catch (error) {
      toast.error('Failed to complete enrollment');
    }
  };

  const handlePaymentFailure = (error) => {
    toast.error(error || 'Payment failed');
    setShowPaymentModal(false);
    setSelectedBatch(null);
    setEnrollingBatchId(null);
  };

  const getPaymentData = () => {
    if (!selectedBatch || !currentUser) return null;

    return {
      amount: selectedBatch.price || 0,
      currency: 'INR',
      type: 'batch_enrollment',
      paymentType: 'batch_enrollment',
      description: `Batch Enrollment: ${selectedBatch.name}`,
      merchantName: 'Mahstry Private Limited',
      customerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
      customerEmail: currentUser.email,
      customerPhone: currentUser.phoneNumber || '',
      userId: currentUser.uid,
      coachingId: selectedBatch.coachingId || null, // Include coachingId to determine GST vs platform fee
      metadata: {
        batchId: selectedBatch.id,
        batchName: selectedBatch.name,
        userId: currentUser.uid,
        coachingId: selectedBatch.coachingId || null,
        type: 'batch_enrollment'
      }
    };
  };

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <InfoIcon className="w-6 h-6" />
                  <h2 className="text-2xl font-bold">Available Batches</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="mt-2 text-blue-100 text-sm">
                Enroll in batches to access classes, recordings, and study materials
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : batches.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg">No batches available at the moment</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {batches.map((batch) => {
                    const startDate = batch.startDate ? (batch.startDate?.toDate ? batch.startDate.toDate() : new Date(batch.startDate)) : null;
                    const endDate = batch.endDate ? (batch.endDate?.toDate ? batch.endDate.toDate() : new Date(batch.endDate)) : null;
                    
                    return (
                      <motion.div
                        key={batch.id}
                        className={`border rounded-lg p-5 transition-all ${
                          batch.isEnrolled
                            ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                              {batch.name}
                            </h3>
                            {batch.isEnrolled && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                <span>Enrolled</span>
                              </span>
                            )}
                          </div>
                          {batch.price && batch.price > 0 && (
                            <div className="flex items-center space-x-1 text-lg font-bold text-blue-600 dark:text-blue-400">
                              <DollarSign className="w-5 h-5" />
                              <span>₹{batch.price}</span>
                            </div>
                          )}
                        </div>

                        {batch.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                            {batch.description}
                          </p>
                        )}

                        <div className="space-y-2 mb-4">
                          {startDate && endDate && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
                            </div>
                          )}
                          {batch.instructorName && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Users className="w-4 h-4" />
                              <span>Instructor: {batch.instructorName}</span>
                            </div>
                          )}
                          {batch.price === 0 || !batch.price ? (
                            <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400 font-medium">
                              <CheckCircle className="w-4 h-4" />
                              <span>Free Enrollment</span>
                            </div>
                          ) : null}
                        </div>

                        {!batch.isEnrolled && (
                          <motion.button
                            onClick={() => handleEnrollClick(batch)}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {batch.price && batch.price > 0 ? `Enroll for ₹${batch.price}` : 'Enroll Now (Free)'}
                          </motion.button>
                        )}

                        {batch.isEnrolled && (
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/batch/${batch.id}`);
                            }}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                          >
                            Go to Batch
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Payment Modal */}
      {showPaymentModal && selectedBatch && getPaymentData() && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBatch(null);
            setEnrollingBatchId(null);
          }}
          paymentData={getPaymentData()}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          title={`Enroll in ${selectedBatch.name}`}
        />
      )}
    </>
  );
};

export default BatchListingModal;

